import { getActiveProfileId } from './active-profile.js';
import type { Role } from './utils/roles.js';

async function request<T>(
  path: string,
  opts: { method?: string; body?: unknown } = {},
  extraHeaders?: Record<string, string>,
): Promise<T> {
  const headers: Record<string, string> = {};
  if (opts.body) headers['Content-Type'] = 'application/json';
  const activeProfile = getActiveProfileId();
  if (activeProfile) headers['X-Active-Profile'] = activeProfile;
  if (extraHeaders) Object.assign(headers, extraHeaders);
  const res = await fetch(path, {
    method: opts.method ?? 'GET',
    headers,
    credentials: 'include',
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  });
  if (!res.ok) {
    let details: unknown;
    try {
      details = await res.json();
    } catch {
      details = await res.text();
    }
    const err = new Error(`http_${res.status}`) as Error & { status: number; details: unknown };
    err.status = res.status;
    err.details = details;
    throw err;
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export interface Me {
  id: string;
  email: string;
  name: string;
  role: Role;
}

export interface CheckinRow {
  id: string;
  name: string;
  email: string;
  phone: string;
  ticketNumber: string | null;
  ticketType: string;
  checkedInAt: string | null;
  vip: boolean;
}

export interface CheckinList {
  total: number;
  checkedIn: number;
  rows: CheckinRow[];
}

export interface Product {
  id: string;
  slug: string;
  name: string;
  receiptName: string;
  description: string;
  priceRub: number;
  priceIls: number;
  merchant: string;
  allpayInstallmentsEnabled: boolean;
  allpayMaxInstallments: number;
  subscriptionEnabled: boolean;
  sendpulseBookId: string | null;
  notionDatabaseId: string | null;
  flowId: string | null;
  telegramBotId: string | null;
  successUrl: string;
  failUrl: string;
  postPurchaseUrl: string;
  postPurchaseLabel: string;
  salesLimit: number | null;
  shortUrl: string | null;
  isActive: boolean;
  profileId: string | null;
  ticketingEnabled: boolean;
  ticketPrefix: string;
  ticketPadding: number;
  ticketStart: number;
  ticketCounter: number;
  // Событие билета: картинка, Wallet и календарь. Время — ISO (UTC).
  eventName: string;
  eventStartsAt: string | null;
  eventEndsAt: string | null;
  eventVenue: string;
  eventAddress: string;
  eventGeo: string;
  eventColor: string;
  eventUrl: string;
  /** Описание события на обороте карточки Wallet. */
  eventDescription: string;
  /** design.md карточки Wallet. */
  walletDesign: string;
  checkinEnabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export type FlowStepType =
  | 'sendpulse_email'
  | 'smtp_email'
  | 'issue_ticket'
  | 'messenger'
  // Сняты с вооружения: операционка всегда выполняется по настройкам продукта.
  // Тип оставлен, потому что у старых флоу такие шаги ещё лежат в БД.
  | 'notion_order'
  | 'telegram_operator'
  | 'sendpulse_book';

export interface FlowStep {
  id?: string;
  type: FlowStepType;
  config: Record<string, unknown>;
  isActive: boolean;
}

export interface Flow {
  id: string;
  profileId: string;
  name: string;
  isActive: boolean;
  steps?: FlowStep[];
  _count?: { steps: number; products: number };
  createdAt: string;
  updatedAt: string;
}

export interface FlowInput {
  name: string;
  isActive?: boolean;
  steps?: { type: FlowStepType; config: Record<string, unknown>; isActive?: boolean }[];
}

export interface ProfileSummary {
  id: string;
  name: string;
  isActive: boolean;
}

export interface ProfileFull extends ProfileSummary {
  yookassaShopId: string;
  yookassaSecretKey: string;
  allpayLogin: string;
  allpayApiKey: string;
  allpayVatEnabled: boolean;
  sendpulseClientId: string;
  sendpulseClientSecret: string;
  sendpulseFromEmail: string;
  sendpulseFromName: string;
  telegramBotToken: string;
  telegramChatId: string;
  telegramThreadId: string;
  shorturlApiUrl: string;
  shorturlApiKey: string;
  notionToken: string;
  notionDatabaseId: string;
  notionClientsDatabaseId: string;
  walletwalletApiKey: string;
  /** Переключатель «Выпускать Wallet-билеты»; без ключа не действует. */
  walletEnabled: boolean;
  productsCount?: number;
  createdAt: string;
  updatedAt: string;
}

export interface WalletPassField {
  label?: string;
  value: string;
}

/** Предпросмотр билета: замечания к событию, Wallet профиля, картинка и тело карточки. */
export interface TicketPreview {
  problems: string[];
  /** on — Wallet включён; off — выключен переключателем; no_key — нет ключа. */
  wallet: 'on' | 'off' | 'no_key';
  image: string | null;
  qr: string | null;
  pass: {
    color?: string;
    colorPreset?: string;
    logoText?: string;
    logoURL?: string;
    wideLogoURL?: string;
    stripURL?: string;
    barcodeAltText?: string;
    headerFields?: WalletPassField[];
    primaryFields?: WalletPassField[];
    secondaryFields?: WalletPassField[];
    backFields?: WalletPassField[];
  } | null;
}

/** Что выпуск карточки Wallet понял из design.md (apps/api/src/services/wallet-design.ts). */
export interface WalletDesignPreview {
  color?: string;
  colorPreset?: string;
  images: Partial<Record<'logoURL' | 'wideLogoURL' | 'thumbnailURL' | 'stripURL' | 'backgroundURL' | 'iconURL', string>>;
  labels: Partial<Record<'ticket' | 'guest' | 'venue' | 'date', string>>;
  back: Array<{ label?: string; value: string }>;
  warnings: string[];
}

export type ProfileInput = Partial<Omit<ProfileFull, 'id' | 'createdAt' | 'updatedAt' | 'productsCount'>> & {
  name: string;
};

export interface SubscriptionPlan {
  id: string;
  profileId: string;
  slug: string;
  name: string;
  description: string;
  priceIls: number;
  merchant: string;
  successUrl: string;
  failUrl: string;
  shortUrl: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export type SubscriptionPlanInput = Omit<
  SubscriptionPlan,
  'id' | 'profileId' | 'shortUrl' | 'createdAt' | 'updatedAt'
>;

export type SubscriptionStatusValue =
  | 'pending'
  | 'active'
  | 'cancelled'
  | 'completed'
  | 'failed';

export interface SubscriptionListItem {
  id: string;
  // A subscription is anchored to EITHER a legacy plan OR a product (+payment).
  planId: string | null;
  productId: string | null;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  status: SubscriptionStatusValue;
  startedAt: string | null;
  cancelledAt: string | null;
  cancelledBy: string | null;
  createdAt: string;
  updatedAt: string;
  plan: { id: string; slug: string; name: string; priceIls: number } | null;
  product: { id: string; slug: string; name: string } | null;
  payment: { amount: number; currency: string } | null;
  _count: { payments: number };
}

export interface SubscriptionPaymentRow {
  id: string;
  amount: number;
  currency: string;
  paymentNumber: number;
  createdAt: string;
}

export interface SubscriptionDetail extends SubscriptionListItem {
  plan: { id: string; slug: string; name: string; priceIls: number; profileId: string } | null;
  product: { id: string; slug: string; name: string; priceIls: number; profileId: string } | null;
  payments: SubscriptionPaymentRow[];
}

export interface User {
  id: string;
  email: string;
  name: string;
  role: Role;
  /** У админа — только профили из его списка; остальные считает otherProfilesCount. */
  allowedProfileIds: string[];
  /** Сколько профилей пользователю выдали другие админы (видно только админу). */
  otherProfilesCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateUserInput {
  name: string;
  email: string;
  password?: string;
  role: 'superadmin' | 'admin' | 'manager';
  allowedProfileIds?: string[];
}

export interface UpdateUserInput {
  name?: string;
  email?: string;
  role?: 'superadmin' | 'admin' | 'manager';
  allowedProfileIds?: string[];
}

/** Бот выдачи билетов в настройках профиля; token — маска «••••» + 4 символа. */
export interface TelegramBot {
  id: string;
  profileId: string;
  name: string;
  username: string;
  token: string;
  /** Чат поддержки (id группы) и топик; пусто — поддержки нет. */
  supportChatId: string;
  supportThreadId: string;
  webhookUrl: string;
  webhookSetAt: string | null;
  webhookError: string;
  createdAt: string;
  updatedAt: string;
}

export interface TelegramBotOption {
  id: string;
  name: string;
  username: string;
}

export interface Snippet {
  snippet: string;
  standaloneUrl: string;
  shortUrl: string | null;
}

export type PromoDiscountType = 'percent' | 'fixed';

export interface PromoCode {
  id: string;
  profileId: string;
  code: string;
  description: string;
  discountType: PromoDiscountType;
  // type=percent
  percentValue: number | null;      // 1-100
  // type=fixed (одна из двух или обе)
  fixedIls: number | null;          // minor units
  fixedRub: number | null;          // minor units
  appliesToAllProducts: boolean;
  productIds: string[];             // пусто если appliesToAllProducts=true
  maxUses: number | null;
  usedCount: number;
  expiresAt: string | null;         // ISO datetime (с таймзоной)
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface PromoCodeUsage {
  id: string;
  customerName: string;
  customerEmail: string;
  amount: number;
  originalAmount: number | null;
  discountAmount: number | null;
  currency: string;
  status: 'pending' | 'succeeded' | 'failed' | 'canceled';
  ticketNumber: string | null;
  createdAt: string;
}

// Использование на агрегированном списке (в PromoCodesList → вкладка «Использования»)
export interface PromoCodeUsageGlobal extends PromoCodeUsage {
  promoCodeId: string | null;
  promoCode: { id: string; code: string } | null;
  productId: string | null;
  product: { id: string; name: string; slug: string } | null;
}

export interface PromoCodeDetail extends PromoCode {
  usages: PromoCodeUsage[];
}

export interface PromoCodeInput {
  code: string;
  description?: string;
  discountType: PromoDiscountType;
  percentValue?: number | null;
  fixedIls?: number | null;
  fixedRub?: number | null;
  appliesToAllProducts: boolean;
  productIds?: string[];
  maxUses?: number | null;
  expiresAt?: string | null;        // ISO datetime
  isActive?: boolean;
}

export const api = {
  login: (email: string, password: string) =>
    request<Me>('/admin/api/auth/login', { method: 'POST', body: { email, password } }),
  logout: () => request<{ ok: true }>('/admin/api/auth/logout', { method: 'POST' }),
  me: () => request<Me>('/admin/api/me'),
  forgotPassword: (email: string) =>
    request<{ ok: true }>('/api/auth/forgot-password', { method: 'POST', body: { email } }),
  resetPassword: (token: string, password: string) =>
    request<{ ok: true }>('/api/auth/reset-password', {
      method: 'POST',
      body: { token, password },
    }),

  listProducts: () => request<Product[]>('/admin/api/products'),
  getProduct: (id: string) => request<Product>(`/admin/api/products/${id}`),
  ticketButtonsHtml: () => request<{ html: string }>('/admin/api/flows/ticket-buttons-html'),
  walletStatus: () => request<{ enabled: boolean }>('/admin/api/wallet-status'),
  ticketPreview: (body: Record<string, unknown>) =>
    request<TicketPreview>('/admin/api/ticket-preview', { method: 'POST', body }),
  previewWalletDesign: (text: string) =>
    request<WalletDesignPreview>('/admin/api/wallet-design/preview', { method: 'POST', body: { text } }),
  createProduct: (data: Partial<Product>) =>
    request<Product>('/admin/api/products', { method: 'POST', body: data }),
  updateProduct: (id: string, data: Partial<Product>) =>
    request<Product>(`/admin/api/products/${id}`, { method: 'PATCH', body: data }),
  deleteProduct: (id: string) =>
    request<void>(`/admin/api/products/${id}`, { method: 'DELETE' }),
  getSnippet: (id: string) => request<Snippet>(`/admin/api/products/${id}/snippet`),
  regenerateShortUrl: (id: string) =>
    request<Product>(`/admin/api/products/${id}/regenerate-short-url`, { method: 'POST' }),

  listFlows: () => request<Flow[]>('/admin/api/flows'),

  // Боты выдачи билетов. Список без секретов — для карточки продукта;
  // управление — в настройках профиля.
  listTelegramBots: () => request<TelegramBotOption[]>('/admin/api/telegram-bots'),
  listProfileTelegramBots: (profileId: string) =>
    request<TelegramBot[]>(`/admin/api/profiles/${profileId}/telegram-bots`),
  createTelegramBot: (profileId: string, data: { name: string; token: string }) =>
    request<TelegramBot & { canJoinGroups: boolean }>(`/admin/api/profiles/${profileId}/telegram-bots`, {
      method: 'POST',
      body: data,
    }),
  updateTelegramBot: (
    id: string,
    data: { name?: string; token?: string; supportChatId?: string; supportThreadId?: string },
  ) =>
    request<TelegramBot>(`/admin/api/telegram-bots/${id}`, { method: 'PATCH', body: data }),
  registerTelegramBotWebhook: (id: string) =>
    request<TelegramBot>(`/admin/api/telegram-bots/${id}/register-webhook`, { method: 'POST' }),
  deleteTelegramBot: (id: string) =>
    request<void>(`/admin/api/telegram-bots/${id}`, { method: 'DELETE' }),
  getFlow: (id: string) => request<Flow>(`/admin/api/flows/${id}`),
  createFlow: (data: FlowInput) => request<Flow>('/admin/api/flows', { method: 'POST', body: data }),
  updateFlow: (id: string, data: Partial<FlowInput>) =>
    request<Flow>(`/admin/api/flows/${id}`, { method: 'PATCH', body: data }),
  deleteFlow: (id: string) => request<void>(`/admin/api/flows/${id}`, { method: 'DELETE' }),

  listSendpulseBooks: () =>
    request<{ id: string; name: string }[]>('/admin/api/integrations/sendpulse/books'),
  listSendpulseTemplates: () =>
    request<{ id: string; name: string }[]>('/admin/api/integrations/sendpulse/templates'),
  listNotionDatabases: (profileId?: string) =>
    request<{ id: string; name: string; isDefault: boolean }[]>(
      '/admin/api/integrations/notion/databases',
      {},
      profileId ? { 'X-Active-Profile': profileId } : undefined,
    ),
  listNotionDatabasesByToken: (token: string) =>
    request<{ id: string; name: string }[]>(
      '/admin/api/integrations/notion/databases-preview',
      { method: 'POST', body: { token } },
    ),

  listProfiles: () => request<(ProfileFull | ProfileSummary)[]>('/admin/api/profiles'),
  getProfile: (id: string) => request<ProfileFull>(`/admin/api/profiles/${id}`),
  createProfile: (data: ProfileInput) =>
    request<ProfileFull>('/admin/api/profiles', { method: 'POST', body: data }),
  updateProfile: (id: string, data: Partial<ProfileInput>) =>
    request<ProfileFull>(`/admin/api/profiles/${id}`, { method: 'PATCH', body: data }),
  deleteProfile: (id: string) =>
    request<void>(`/admin/api/profiles/${id}`, { method: 'DELETE' }),
  copyProfile: (id: string) =>
    request<ProfileFull>(`/admin/api/profiles/${id}/copy`, { method: 'POST' }),

  listUsers: () => request<User[]>('/admin/api/users'),
  getUser: (id: string) => request<User>(`/admin/api/users/${id}`),
  createUser: (data: CreateUserInput) =>
    request<User>('/admin/api/users', { method: 'POST', body: data }),
  updateUser: (id: string, data: UpdateUserInput) =>
    request<User>(`/admin/api/users/${id}`, { method: 'PATCH', body: data }),
  deleteUser: (id: string) => request<void>(`/admin/api/users/${id}`, { method: 'DELETE' }),
  sendUserPasswordReset: (id: string) =>
    request<void>(`/admin/api/users/${id}/send-password-reset`, { method: 'POST' }),

  listPlans: () => request<SubscriptionPlan[]>('/admin/api/plans'),
  getPlan: (id: string) => request<SubscriptionPlan>(`/admin/api/plans/${id}`),
  createPlan: (data: SubscriptionPlanInput) =>
    request<SubscriptionPlan>('/admin/api/plans', { method: 'POST', body: data }),
  updatePlan: (id: string, data: Partial<SubscriptionPlanInput>) =>
    request<SubscriptionPlan>(`/admin/api/plans/${id}`, { method: 'PATCH', body: data }),
  deletePlan: (id: string) =>
    request<void>(`/admin/api/plans/${id}`, { method: 'DELETE' }),
  getPlanSnippet: (id: string) =>
    request<{ standaloneUrl: string; shortUrl: string | null }>(
      `/admin/api/plans/${id}/snippet`,
    ),

  listSubscriptions: () => request<SubscriptionListItem[]>('/admin/api/subscriptions'),
  getSubscription: (id: string) =>
    request<SubscriptionDetail>(`/admin/api/subscriptions/${id}`),
  cancelSubscription: (id: string) =>
    request<SubscriptionListItem>(`/admin/api/subscriptions/${id}/cancel`, {
      method: 'POST',
    }),
  syncSubscriptionStatus: (id: string) =>
    request<{ ok: boolean; providerStatus?: number; mappedTo?: string; paymentsN?: number }>(
      `/admin/api/subscriptions/${id}/sync-status`,
      { method: 'POST' },
    ),

  listPromoCodes: () => request<PromoCode[]>('/admin/api/promo-codes'),
  listPromoCodeUsages: () =>
    request<PromoCodeUsageGlobal[]>('/admin/api/promo-codes/usages'),
  getPromoCode: (id: string) => request<PromoCodeDetail>(`/admin/api/promo-codes/${id}`),
  createPromoCode: (data: PromoCodeInput) =>
    request<PromoCode>('/admin/api/promo-codes', { method: 'POST', body: data }),
  updatePromoCode: (id: string, data: Partial<PromoCodeInput>) =>
    request<PromoCode>(`/admin/api/promo-codes/${id}`, { method: 'PATCH', body: data }),
  deletePromoCode: (id: string) =>
    request<void>(`/admin/api/promo-codes/${id}`, { method: 'DELETE' }),

  // Comp-tickets — выпуск промобилетов (без оплаты)
  listCompTickets: () => request<CompTicket[]>('/admin/api/comp-tickets'),
  getCompTicketNextNumber: (productId: string, prefix: string) =>
    request<{ nextNumber: number }>(
      `/admin/api/comp-tickets/next-number?productId=${encodeURIComponent(
        productId,
      )}&prefix=${encodeURIComponent(prefix)}`,
    ),
  createCompTickets: (data: CompTicketsInput) =>
    request<CompTicketsResult>('/admin/api/comp-tickets', { method: 'POST', body: data }),

  // Чек-ин на входе. Префикс /api/checkin/* (вне /admin): волонтёрам admin-API
  // закрыт (раньше /admin ещё закрывал Cloudflare Access, его сняли 13.10.2026).
  // Своя пара login/me/logout.
  checkinLogin: (email: string, password: string) =>
    request<Me>('/api/checkin/login', { method: 'POST', body: { email, password } }),
  checkinLogout: () => request<{ ok: true }>('/api/checkin/logout', { method: 'POST' }),
  checkinMe: () => request<Me>('/api/checkin/me'),
  checkinList: () => request<CheckinList>('/api/checkin/list'),
  checkinMark: (id: string) =>
    request<CheckinRow>(`/api/checkin/${id}/checkin`, { method: 'POST' }),
  checkinUndo: (id: string) =>
    request<CheckinRow>(`/api/checkin/${id}/undo`, { method: 'POST' }),
  checkinSyncVip: () =>
    request<{ vip: number; scanned: number }>('/api/checkin/sync-vip', { method: 'POST' }),
};

export interface CompTicket {
  id: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  ticketNumber: string | null;
  ticketIssuedAt: string | null;
  createdAt: string;
  status: 'pending' | 'succeeded' | 'failed' | 'canceled';
  productId: string;
  product: { id: string; name: string; slug: string } | null;
}

export interface CompTicketCustomer {
  name: string;
  email: string;
  phone: string;
  messengerType?: 'telegram' | 'whatsapp' | null;
  messengerValue?: string | null;
}

export interface CompTicketsInput {
  productId: string;
  prefix: string;
  padding: number;
  startNumber: number;
  currency: 'ILS' | 'RUB';
  customers: CompTicketCustomer[];
}

export interface CompTicketsResult {
  created: number;
  results: Array<{
    ticketNumber: string;
    ok: boolean;
    paymentId?: string;
    error?: string;
  }>;
}
